import { CommonButton } from 'components/common/Forms/Button';
import { DatePickerInput } from 'components/common/Forms/DatePickerInput';
import Select from 'components/common/Forms/Select';
import { useCommonTranslation } from 'hooks/i18n/useCommonTranslation';
import { WaitingListFormValues } from 'hooks/patient/waitingList/useWaitingList';
import { get } from 'lodash';
import { Controller, FieldError, useFormContext } from 'react-hook-form';
import { WaitingListRow as WaitingListRowStyled } from './WaitingList.styled';
import { useWaitingListRowData } from './WaitingListRowHooks';
import { WaitingListRowsProps } from './WaitingListRows';

export type WaitingListRowProps = WaitingListRowsProps & {
  index: number;
};

export const WaitingListRow = ({
  index,
  slots,
  closeHour,
  minDate,
  maxDate,
  isDayAvailable,
}: WaitingListRowProps) => {
  const { t } = useCommonTranslation();
  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext<WaitingListFormValues>();

  const { hourTo, hourFromOptions, hourToOptions } = useWaitingListRowData({
    index,
    slots,
    closeHour,
  });

  /**
   * Add numeric suffix to labels to meet WCAG 2.2 - 2.4.6 Headings and Labels (Level AA)
   * https://www.w3.org/WAI/WCAG22/Understanding/headings-and-labels.html
   */
  const labelSuffix = ` #${index + 1}`;

  return (
    <WaitingListRowStyled>
      <Controller
        control={control}
        name={`list.${index}.date`}
        render={({ field }) => {
          const error: FieldError = get(
            errors,
            `list.${index}.date`,
          ) as unknown as FieldError;
          return (
            <DatePickerInput
              minDate={minDate}
              maxDate={maxDate}
              filterDate={isDayAvailable}
              label={`${t('date')}${labelSuffix}`}
              noSpaceForHelperText
              name={field.name}
              onChange={(date) => field.onChange(date)}
              value={field.value || undefined}
              hasError={!!error}
              helperText={error?.message}
              flexWidth
            />
          );
        }}
      />
      <Controller
        control={control}
        name={`list.${index}.hourFrom`}
        render={({ field }) => {
          const error: FieldError = get(
            errors,
            `list.${index}.hourFrom`,
          ) as unknown as FieldError;
          return (
            <Select
              label={`${t('hour-from')}${labelSuffix}`}
              options={hourFromOptions}
              value={field.value}
              name={field.name}
              onChange={(hourFromValue) => {
                const hourFrom = hourFromValue as number;
                field.onChange(hourFrom);
                if (hourTo && hourTo <= hourFrom) {
                  setValue(`list.${index}.hourTo`, null);
                }
              }}
              hasError={!!error}
              helperText={error?.message}
              flexWidth
              noSpaceForHelperText
            />
          );
        }}
      />
      <Controller
        control={control}
        name={`list.${index}.hourTo`}
        render={({ field }) => {
          const error: FieldError = get(
            errors,
            `list.${index}.hourTo`,
          ) as unknown as FieldError;

          return (
            <Select
              label={`${t('hour-to')}${labelSuffix}`}
              options={hourToOptions}
              value={field.value}
              name={field.name}
              onChange={(hourFrom) => field.onChange(hourFrom)}
              hasError={!!error}
              helperText={error?.message}
              flexWidth
              noSpaceForHelperText
            />
          );
        }}
      />
      <CommonButton
        type="button"
        variant="danger"
        onClick={() => {
          setValue(`list.${index}.date`, null, { shouldDirty: true });
          setValue(`list.${index}.hourFrom`, null, { shouldDirty: true });
          setValue(`list.${index}.hourTo`, null, { shouldDirty: true });
        }}
      >
        {t('clear')}
      </CommonButton>
    </WaitingListRowStyled>
  );
};
